'use client';

import { useEffect, useRef, type KeyboardEvent } from 'react';

import { TRACKED_EVENT_POSITION } from '@/constants';
import { useAddClickButtonGtag } from '@/hooks/useGtag.hook';

import { carouselNavLabel } from './constants';

// 캐러셀의 roving 키보드 상호작용을 캡슐화한 훅.
// 반환한 { containerRef, onKeyDown } 을 캐러셀 컨테이너에 연결하면:
//  · 카드에서   ← → : 캐러셀 회전(포커스는 새 중앙 카드로 따라감)
//  · 카드에서   ↓    : 화살표 버튼으로 이동
//  · 화살표에서 ↑    : 중앙 카드로 이동
//  · 화살표에서 ← → : 두 화살표 버튼 간 이동
//
// 포커스가 어느 구역에 있는지는 컨테이너 하위의 data-* 표식으로 판별한다
// (아래 셀렉터 ↔ desktop-fan.tsx/nav-arrow.tsx 의 data 속성이 짝을 이룸).
const SELECTOR = {
  card: '[data-card]',
  arrowGroup: '[data-nav]',
  centerCardLink: '[data-center] a[href]',
  arrow: (dir: 'prev' | 'next') => `[data-nav="${dir}"]`,
};

export function useRovingCarousel({
  active,
  goTo,
}: {
  active: number;
  goTo: (index: number) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  // 키보드 회전 직후에만 새 중앙 카드로 포커스를 옮긴다(마우스 회전 땐 포커스를 뺏지 않음).
  const focusCenterAfterRotate = useRef(false);

  // 키보드 이동도 화살표 버튼과 같은 이벤트·라벨로 추적(method=keyboard 로 구분)
  const trackPrev = useAddClickButtonGtag({
    label: carouselNavLabel('prev'),
    position: TRACKED_EVENT_POSITION.MAIN_CONTENT,
  });
  const trackNext = useAddClickButtonGtag({
    label: carouselNavLabel('next'),
    position: TRACKED_EVENT_POSITION.MAIN_CONTENT,
  });

  const focusWithin = (selector: string) =>
    containerRef.current?.querySelector<HTMLElement>(selector)?.focus();

  // 회전(active 변경)으로 중앙 카드 DOM 이 교체된 뒤, 키보드로 넘긴 경우에만 새 중앙에 포커스.
  useEffect(() => {
    if (!focusCenterAfterRotate.current) return;
    focusCenterAfterRotate.current = false;
    focusWithin(SELECTOR.centerCardLink);
  }, [active]);

  const rotate = (dir: -1 | 1) => {
    (dir < 0 ? trackPrev : trackNext)(undefined, { method: 'keyboard' });
    focusCenterAfterRotate.current = true;
    goTo(active + dir);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const zone = target.closest(SELECTOR.arrowGroup)
      ? 'arrows'
      : target.closest(SELECTOR.card)
        ? 'card'
        : null;
    if (!zone) return;

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowRight': {
        const dir = e.key === 'ArrowLeft' ? -1 : 1;
        e.preventDefault();
        if (zone === 'card') rotate(dir);
        else focusWithin(SELECTOR.arrow(dir < 0 ? 'prev' : 'next'));
        break;
      }
      case 'ArrowDown':
        if (zone === 'card') {
          e.preventDefault();
          focusWithin(SELECTOR.arrow('prev'));
        }
        break;
      case 'ArrowUp':
        if (zone === 'arrows') {
          e.preventDefault();
          focusWithin(SELECTOR.centerCardLink);
        }
        break;
    }
  };

  return { containerRef, onKeyDown };
}
